import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Alert, Button, Form, FormGroup } from 'reactstrap';
import BusyButton from '~/components/BusyButton';
import ErrorList from '~/components/ErrorList';
import PasswordInput from '~/components/PasswordInput';
import { getAppContext } from '~/lib/AppContext';
import { PasswordResetTokenResult } from '~/lib/ApiModel';
import { field, is, validate } from '~/lib/helper/ValidationHelper';
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from '~/lib/constants/PasswordValidation';
import FormStore from '~/lib/store/FormStore';
import Loader from '~/components/Loader';

interface ResetFormValues {
    token: string;
    new_password: string;
    confirmPassword: string;
}

function emptyFormValues(): ResetFormValues {
    return {
        token: '',
        new_password: '',
        confirmPassword: '',
    };
}

type ScreenState = 'check' | 'collect' | 'success' | 'link_issue';

@observer
export default class ResetWithTokenPane extends React.Component {
    resetForm = new FormStore<ResetFormValues>(emptyFormValues());

    @observable
    screenState: ScreenState = 'check';

    constructor(props: Record<string, never>) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        this.initFromQuery();
    }

    @action
    private initFromQuery() {
        // Extract token from the current query string (if any)
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token') ?? '';

        // Remove token from the URL to prevent leaking via browser history or referrer headers
        if (token) {
            window.history.replaceState({}, '', window.location.pathname);
        }

        if (!token) {
            window.location.href = '/login/forgotpassword';
            return;
        }

        this.resetForm.init({
            token,
            new_password: '',
            confirmPassword: '',
        });

        this.screenState = 'check';
        void this.resetForm.doSubmit(() => this.doCheck(), { errorSummary: 'Unable to verify reset link' });
    }

    @action
    private handleResetResult(result: PasswordResetTokenResult, successState: ScreenState) {
        switch (result) {
            case 'success':
                this.screenState = successState;
                break;
            case 'token-expired':
                this.resetForm.setValidationErrors({ token: ['The reset link used has expired'] });
                this.screenState = 'link_issue';
                break;
            case 'token-invalid':
                this.resetForm.setValidationErrors({ token: ['The reset link used was invalid'] });
                this.screenState = 'link_issue';
                break;
            case 'error':
                this.resetForm.setValidationErrors({ token: ['Unable to reset password'] });
                this.screenState = 'link_issue';
                break;
        }
    }

    private async doCheck() {
        if (!this.resetForm.validate((values) => validate(values, field('token', is.required())))) {
            this.resetForm.setValidationErrors({ token: ['The reset link used was invalid'] });
            this.screenState = 'link_issue';
            return;
        }

        const { token } = this.resetForm.values;

        const result = await getAppContext().siteApiClient.checkPasswordResetToken({ token });

        this.handleResetResult(result, 'collect');
    }

    private async doReset() {
        // Validate user input
        if (
            !this.resetForm.validate((values) =>
                validate(
                    values,
                    field('token', is.required('Token is required')),
                    field(
                        'new_password',
                        is.requiredNotEmpty('Password is required'),
                        is.minLength(
                            PASSWORD_MIN_LENGTH,
                            `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`
                        ),
                        is.maxLength(
                            PASSWORD_MAX_LENGTH,
                            `Password must be at most ${PASSWORD_MAX_LENGTH} characters long`
                        )
                    ),
                    field(
                        'confirmPassword',
                        is.required('Confirm password is required'),
                        is.invalidIf((c) => c !== values.new_password, 'Passwords do not match')
                    )
                )
            )
        ) {
            return;
        }

        const { token, new_password } = this.resetForm.values;

        const result = await getAppContext().siteApiClient.resetPasswordUsingToken({
            token,
            new_password,
        });

        this.handleResetResult(result, 'success');
    }

    renderCollect() {
        return (
            <>
                <div className="preLoginTitle">
                    <h2>Reset Password</h2>
                    <p className="mb-3">
                        Enter your new password and password confirmation below and click the 'Save Password' button.
                    </p>
                    <p>Please ensure that you pick a strong password that you have not used anywhere else.</p>
                </div>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void this.resetForm.doSubmit(() => this.doReset(), {
                            errorSummary: 'Unable to reset password',
                        });
                    }}
                >
                    <FormGroup className="mb-3">
                        <label className="form-label" htmlFor={this.resetForm.idFor('new_password')}>
                            New Password
                        </label>
                        <PasswordInput
                            id={this.resetForm.idFor('new_password')}
                            placeholder="Enter new password"
                            autoComplete="new-password"
                            {...this.resetForm.inputProps('new_password')}
                        />
                        <ErrorList errors={this.resetForm.errorsFor('new_password')} />
                    </FormGroup>

                    <FormGroup className="mb-4">
                        <label className="form-label" htmlFor={this.resetForm.idFor('confirmPassword')}>
                            Confirm New Password
                        </label>
                        <PasswordInput
                            id={this.resetForm.idFor('confirmPassword')}
                            placeholder="Confirm new password"
                            autoComplete="new-password"
                            {...this.resetForm.inputProps('confirmPassword')}
                        />
                        <ErrorList errors={this.resetForm.errorsFor('confirmPassword')} />
                    </FormGroup>
                    <div className="loginActions mb-3">
                        <BusyButton
                            type="submit"
                            busy={this.resetForm.isSubmitting}
                            color="primary"
                            size="lg"
                            className="w-100"
                        >
                            <i className="icon ion-md-refresh me-2" />
                            Save Password
                        </BusyButton>
                    </div>
                </Form>
            </>
        );
    }

    renderSuccess() {
        return (
            <>
                <div className="preLoginTitle">
                    <h2>Reset Password</h2>
                </div>
                <Alert color="success" className="mb-4">
                    <i className="icon ion-md-checkmark-circle me-2" />
                    Your password has been updated. You can now log in with your new password.
                </Alert>
                <div className="loginActions mb-3">
                    <Button type="button" color="primary" size="lg" href="/login" className="w-100" autoFocus>
                        <i className="icon ion-md-arrow-back me-2" />
                        Back to Log In
                    </Button>
                </div>
            </>
        );
    }

    renderLinkIssue() {
        return (
            <>
                <div className="preLoginTitle">
                    <h2>Reset Password</h2>
                </div>
                <Alert color="danger" className="mb-4" role="alert">
                    <p>
                        <i className="icon ion-md-alert me-2" />
                        We encountered an error when trying to reset your password.
                    </p>
                    {this.resetForm.errorsFor('token')?.map((error) => (
                        <p key={error}>{error}</p>
                    ))}
                    <p>Please try again.</p>
                </Alert>
                <Button
                    type="button"
                    color="primary"
                    size="lg"
                    href="/login/forgotpassword"
                    className="w-100"
                    autoFocus
                >
                    <i className="icon ion-md-arrow-back me-2" />
                    Return to Reset Password
                </Button>
            </>
        );
    }

    renderCheck() {
        return (
            <>
                <div className="preLoginTitle">
                    <h2>Reset Password</h2>
                </div>

                <Loader size="sm" text="Validating reset link..." />
            </>
        );
    }

    render() {
        return (
            <>
                {this.screenState === 'check' && this.renderCheck()}
                {this.screenState === 'collect' && this.renderCollect()}
                {this.screenState === 'success' && this.renderSuccess()}
                {this.screenState === 'link_issue' && this.renderLinkIssue()}
            </>
        );
    }
}
